/**
 * Internationalisation: i18next with English and Simplified Chinese. English
 * is bundled (it is the fallback); Chinese is a small chunk loaded before
 * the first render when it is the language, or on switching. Every
 * user-facing string goes through `t()`; keys are type-checked against
 * `locales/en.json`, and a test keeps `zh-CN.json` in step with it.
 *
 * The language is, in order: the person's saved choice, the browser's
 * preferred languages, then English. The `<html lang>` attribute follows it,
 * so screen readers and fonts switch too.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';

/** The languages Hire is written in. */
export const LANGUAGES = ['en', 'zh-CN'] as const;

/** One of `LANGUAGES`. */
export type Language = (typeof LANGUAGES)[number];

/** Each language's name in itself, for the language switcher. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  'zh-CN': '简体中文',
};

const STORAGE_KEY = 'hire.lang';

/** Whether `value` is a supported language code. */
export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

/**
 * Picks the supported language closest to a list of BCP 47 tags (as in
 * `navigator.languages`). Any Chinese tag maps to `zh-CN`, any English one
 * to `en`; the first match wins.
 *
 * @param preferred - Tags in order of preference.
 */
export function matchLanguage(preferred: readonly string[]): Language {
  for (const tag of preferred) {
    const primary = tag.toLowerCase().split('-')[0];
    if (primary === 'zh') return 'zh-CN';
    if (primary === 'en') return 'en';
  }
  return 'en';
}

/** The saved choice, or the best match for the browser, or English. */
export function detectLanguage(): Language {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(saved)) return saved;
  } catch {
    // Storage may be unavailable; fall back to the browser's languages.
  }
  return matchLanguage(typeof navigator === 'undefined' ? [] : navigator.languages);
}

/** The i18next instance the app uses. */
export const i18n = i18next.createInstance();

/** Each language's strings; English is already in the bundle. */
const LOADERS: Record<Language, () => Promise<{ default: typeof en }>> = {
  en: () => Promise.resolve({ default: en }),
  'zh-CN': () => import('./locales/zh-CN.json'),
};

/** Languages whose main strings are in (English is bundled). */
const loaded = new Set<Language>(['en']);

/**
 * Loads a language's strings into i18next, once. Merged into what is there,
 * since a lazily loaded feature may have added its own strings first
 * (`account-strings.ts`).
 */
export async function loadLanguage(language: Language): Promise<void> {
  if (loaded.has(language)) return;
  const { default: strings } = await LOADERS[language]();
  i18n.addResourceBundle(language, 'translation', strings, true, false);
  loaded.add(language);
}

/**
 * Initialises i18next, loading the language's strings first, and sets
 * `<html lang>`. Await it once, before the first render.
 *
 * @param language - Defaults to `detectLanguage()`.
 */
export async function initI18n(language: Language = detectLanguage()): Promise<typeof i18n> {
  if (!i18n.isInitialized) {
    await i18n.use(initReactI18next).init({
      resources: { en: { translation: en } },
      lng: 'en',
      fallbackLng: 'en',
      supportedLngs: [...LANGUAGES],
      returnNull: false,
      interpolation: { escapeValue: false }, // React escapes.
      // Feature dictionaries can arrive after a mounted shell. Refresh its
      // translations too, rather than leaving a missing key until navigation.
      react: { bindI18nStore: 'added removed' },
    });
    i18n.on('languageChanged', (lng) => {
      if (typeof document !== 'undefined') document.documentElement.lang = lng;
    });
  }
  try {
    await loadLanguage(language);
    await i18n.changeLanguage(language);
  } catch {
    // The strings could not load (offline): English, the fallback, stays.
  }
  if (typeof document !== 'undefined') document.documentElement.lang = i18n.language;
  return i18n;
}

/** The current language. */
export function currentLanguage(): Language {
  return isLanguage(i18n.language) ? i18n.language : 'en';
}

/**
 * Switches the language and remembers the choice on this device. (From H1.2
 * a signed-in person's choice is saved to their account too.)
 *
 * @param language - The new language.
 */
export async function setLanguage(language: Language): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Storage may be unavailable; the switch still applies now.
  }
  await loadLanguage(language);
  await i18n.changeLanguage(language);
}
